import type { ColumnFiltersState } from '@tanstack/react-table';
import moment from 'moment';
import type { PriceCardProps } from 'components/price-card';
import type { PriceCurveSeries } from 'components/price-curve-chart/types';
import { formatNumber } from 'utils/number';
import type { PriceDeckParams } from './atoms';
import {
  DECK_TYPE_CALENDAR,
  DECK_TYPE_RELATIVE,
  FALLBACK_DISCRETE_PRICE,
  FALLBACK_HOLD_LAST_STRIP,
  FALLBACK_HOLD_LAST_VALUE,
  STRIP_MODE_AS_OF,
  STRIP_MODE_MOST_RECENT,
  type FallbackType,
  type MethodType,
  type PriceDeckDetail,
  type PriceDeckPreviewResponse,
  type PriceDeckPreviewStats,
  type PriceDeckStatus,
  type StripDateMode,
} from './types';
import type { PriceDeckFormValues } from './price-deck-form/types';

/** Default payload used to trigger the initial fetch on mount. */
export const DEFAULT_PARAMS: PriceDeckParams = {
  page: 1,
  pageSize: 50,
  sortAttribute: 'created_at',
  sortDirection: 'DESC',
};

/** Maps method_type enum values to display labels. */
export const METHOD_TYPE_LABELS: Record<MethodType, string> = {
  flat: 'Flat',
  strip: 'Strip',
  modified_strip: 'Modified Strip',
  custom_upload: 'Custom Upload',
};

/** Maps status enum values to display labels. */
export const STATUS_LABELS: Record<PriceDeckStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  archived: 'Archived',
};

/** Maps frontend column IDs to backend sort field names. */
export const SORT_FIELD_MAP: Record<string, string> = {
  name: 'name',
  methodType: 'method_type',
  stripPriceDate: 'strip_price_date',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
};

/** Maps fallback_type enum values to display labels. */
export const FALLBACK_TYPE_LABELS: Record<FallbackType, string> = {
  discrete_price: 'Discrete Price',
  hold_last_strip: 'Hold Last Strip',
  hold_last_value: 'Hold Last Value',
};

/** Maps strip_date_mode enum values to display labels. */
export const STRIP_DATE_MODE_LABELS: Record<StripDateMode, string> = {
  most_recent: 'Most Recent',
  as_of: 'As Of',
};

/** Dropdown items for pricing method selector. */
export const METHOD_TYPE_ITEMS = Object.entries(METHOD_TYPE_LABELS).map(
  ([value, label]) => ({ id: value, label })
);

/** Dropdown items for strip date mode selector. */
export const STRIP_DATE_MODE_ITEMS = [
  { id: STRIP_MODE_MOST_RECENT, label: 'Most Recent' },
  { id: STRIP_MODE_AS_OF, label: 'As-Of Date' },
];

/** Dropdown items for modified strip fallback type. */
export const FALLBACK_TYPE_ITEMS = [
  { id: FALLBACK_DISCRETE_PRICE, label: 'Discrete Price' },
  { id: FALLBACK_HOLD_LAST_STRIP, label: 'Hold Last Strip' },
];

/** Dropdown items for custom upload fallback type. */
export const UPLOAD_FALLBACK_TYPE_ITEMS = [
  { id: FALLBACK_DISCRETE_PRICE, label: 'Discrete Price' },
  { id: FALLBACK_HOLD_LAST_VALUE, label: 'Hold Last Value' },
];

/** Dropdown items for custom upload deck type. */
export const DECK_TYPE_ITEMS = [
  { id: DECK_TYPE_CALENDAR, label: 'Calendar Dates' },
  { id: DECK_TYPE_RELATIVE, label: 'Relative (Month 1, 2...)' },
];

/**
 * Build price stat cards from preview stats. Shared by form and show pages.
 * @param {PriceDeckPreviewStats | undefined} stats - Preview stats from eval_calculator.
 * @return {PriceCardProps[]} Array of price card props.
 */
export const buildPriceCards = (
  stats: PriceDeckPreviewStats | undefined
): PriceCardProps[] => {
  if (!stats) return [];
  return [
    { label: 'Front Month Oil', value: stats.currentOil, unit: '$/BBL' },
    {
      label: '12-Mo Avg Oil',
      value: stats.avg12moOil,
      unit: '$/BBL forward avg',
    },
    {
      label: '60-Mo Avg Oil',
      value: stats.avg60moOil,
      unit: '$/BBL forward avg',
    },
    { label: 'Front Month Gas', value: stats.currentGas, unit: '$/MMBTU' },
    {
      label: '12-Mo Avg Gas',
      value: stats.avg12moGas,
      unit: '$/MMBTU forward avg',
    },
    {
      label: '60-Mo Avg Gas',
      value: stats.avg60moGas,
      unit: '$/MMBTU forward avg',
    },
  ];
};

/**
 * Converts TanStack ColumnFiltersState to PriceDeckParams filter fields.
 * Clears all filter params first, then sets active ones.
 * @param {ColumnFiltersState} filters - TanStack column filter state.
 * @return {Partial<PriceDeckParams>} Mapped filter params for the search query.
 */
export const columnFiltersToParams = (
  filters: ColumnFiltersState
): Partial<PriceDeckParams> => {
  const params: Partial<PriceDeckParams> = {
    methodType: undefined,
    status: undefined,
    createdBy: undefined,
    stripDateMode: undefined,
    stripPriceDate: undefined,
    createdAt: undefined,
  };

  for (const filter of filters) {
    switch (filter.id) {
      case 'methodType':
        params.methodType = filter.value as string[];
        break;
      case 'status':
        params.status = filter.value as string[];
        break;
      case 'createdBy':
        params.createdBy = filter.value as string[];
        break;
      case 'stripPriceDate': {
        const val = filter.value as { mode: string; date?: string };
        if (val.mode === STRIP_MODE_MOST_RECENT) {
          params.stripDateMode = STRIP_MODE_MOST_RECENT;
          params.stripPriceDate = undefined;
        } else if (val.mode === 'as_of' && val.date) {
          params.stripDateMode = 'as_of';
          params.stripPriceDate = val.date;
        }
        break;
      }
      case 'createdAt': {
        const [start, end] =
          (filter.value as [string | null, string | null]) ?? [];
        params.createdAt =
          start || end
            ? {
                start: start ?? undefined,
                end: end ?? undefined,
              }
            : undefined;
        break;
      }
    }
  }

  return params;
};

/**
 * Derive chart series and date timestamps from preview rows.
 * Shared by the form and show pages.
 * @param {PriceDeckPreviewResponse | null} preview - Preview response from eval_calculator.
 * @param {PriceDeckPreviewResponse | null} stripReference - Optional unscaled strip prices for dotted reference lines.
 * @return {object} Chart series and date timestamps.
 */
export const buildChartData = (
  preview: PriceDeckPreviewResponse | null,
  stripReference?: PriceDeckPreviewResponse | null
): { chartSeries: PriceCurveSeries | null; chartDates: number[] } => {
  if (!preview?.rows?.length) return { chartSeries: null, chartDates: [] };
  const dates = preview.rows.map((r) => moment.utc(r.month).valueOf());
  const series: PriceCurveSeries = {
    oil: preview.rows.map((r, i) => [dates[i], r.oilPrice] as [number, number]),
    gas: preview.rows.map((r, i) => [dates[i], r.gasPrice] as [number, number]),
  };
  if (stripReference?.rows?.length) {
    // Filter out null prices — when noFillForward is set, dates beyond
    // strip data return null. Only plot actual data points.
    const validRows = stripReference.rows.filter(
      (r) => r.oilPrice !== null && r.gasPrice !== null
    );
    if (validRows.length > 0) {
      const refDates = validRows.map((r) => moment.utc(r.month).valueOf());
      series.valOil = validRows.map(
        (r, i) => [refDates[i], r.oilPrice] as [number, number]
      );
      series.valGas = validRows.map(
        (r, i) => [refDates[i], r.gasPrice] as [number, number]
      );
      series.valOilLabel = 'Strip Oil ($/BBL)';
      series.valGasLabel = 'Strip Gas ($/MMBTU)';
      series.valVisible = true;
    }
  }
  return { chartSeries: series, chartDates: dates };
};

/**
 * Format a price value for table display (no unit).
 * @param {number | null} value - The price value.
 * @return {string} Formatted string or '—' if null.
 */
export const formatPrice = (value: number | null): string => {
  if (value === null || value === undefined) return '—';
  return formatNumber({
    value,
    style: 'currency',
    minPrecision: 2,
    maxPrecision: 3,
  });
};

/**
 * Format a numeric value as currency with unit label.
 * @param {number | null | undefined} value - The value to format.
 * @param {string} unit - The unit label (e.g. '$/BBL').
 * @return {string} Formatted string or '--' if null.
 */
export const formatCurrency = (
  value: number | null | undefined,
  unit: string
): string => {
  if (value === null || value === undefined) return '--';
  return `${formatNumber({
    value,
    style: 'currency',
    minPrecision: 2,
    maxPrecision: 2,
  })} ${unit}`.trim();
};

/**
 * Transform API detail response into form values for edit mode.
 * @param {PriceDeckDetail} detail - The deck detail from GET /price_decks/:id.
 * @return {PriceDeckFormValues} Form-ready values.
 */
export function priceDeckDetailToFormValues(
  detail: PriceDeckDetail
): PriceDeckFormValues {
  const cfg = detail.config;
  return {
    name: detail.name,
    description: detail.description ?? '',
    methodType: detail.methodType,
    oilFlatPrice: cfg.oil_flat_price ?? null,
    gasFlatPrice: cfg.gas_flat_price ?? null,
    stripDateMode: detail.stripDateMode ?? STRIP_MODE_MOST_RECENT,
    stripSource: cfg.strip_source ?? null,
    stripPriceDate: detail.stripPriceDate ?? null,
    oilStripDuration: cfg.oil_strip_duration ?? 12,
    oilScaleFactor: cfg.oil_scale_factor ?? 100,
    oilFallbackType: cfg.oil_fallback_type ?? FALLBACK_HOLD_LAST_STRIP,
    gasStripDuration: cfg.gas_strip_duration ?? 12,
    gasScaleFactor: cfg.gas_scale_factor ?? 100,
    gasFallbackType: cfg.gas_fallback_type ?? FALLBACK_HOLD_LAST_STRIP,
    blobPath: cfg.blob_path ?? null,
    originalFilename: cfg.original_filename ?? null,
    deckType: cfg.deck_type ?? DECK_TYPE_CALENDAR,
    anchorDate: cfg.anchor_date ?? null,
  };
}
